import type { Metro } from '../../types/metro';
import { RankingsSeverityPill } from './RankingsSeverityPill';
import type { RankingMetric } from '../../hooks/useRankingMetrics';
import { formatNumber, getStatus, shortageCell } from '../../lib/utils';

interface RankingsRowProps {
  readonly rank: number;
  readonly metro: Metro;
  readonly metric: RankingMetric;
  /**
   * Which column layout to render. 2026-05-18 feedback: when an
   * occupational group is selected the table pivots to per-group
   * numbers (credentials / jobs / ratio / shortage) for that sector.
   * Otherwise the default metro-wide columns render.
   */
  readonly mode?: 'default' | 'lens';
  /**
   * Optional row-click handler -- when present, the row becomes
   * keyboard-activatable (cursor-pointer, aria-haspopup=dialog,
   * Enter / Space fires the same handler). Used by the page-level
   * RowPeekDrawer wiring; omit for static (non-peekable) renders.
   */
  readonly onSelect?: (metro: Metro) => void;
}

/**
 * One row in the rankings table.
 *
 * Default mode (no occupational group lens) — columns:
 *   1. Rank (#)
 *   2. Metro Area
 *   3. Springboard occupational groups (count)
 *   4. Share with shortages (% of sectors in shortage)
 *   5. Credentials-to-jobs ratio (severity pill, continuous gradient)
 *   6. Occupational group with largest shortage
 *
 * Lens mode (an occupational group is selected) — columns:
 *   1. Rank (#)
 *   2. Metro Area
 *   3. Number of credentials (in the lensed sector)
 *   4. Number of jobs (in the lensed sector)
 *   5. Credentials-to-jobs ratio (severity pill, sector ratio)
 *   6. Credential shortage (Georgetown's supplied `shortage` column for the sector)
 *
 * The severity pill always renders the ratio that matches the
 * column layout — metro-wide in default mode, sector-specific in
 * lens mode (via `metric.lensRatio`).
 */
export function RankingsRow({ rank, metro, metric, mode = 'default', onSelect }: RankingsRowProps) {
  const lensActive = mode === 'lens';
  const ratio = lensActive ? metric.lensRatio ?? metro.ratio : metro.ratio;
  const shortageSharePct = Math.round(metric.shortageShare * 100);
  const lensCredentials = metric.lensCredentials ?? 0;
  const lensJobs = metric.lensJobs ?? 0;
  // CR-013: Georgetown's supplied shortage column, read straight through —
  // not reconstructed as max(0, jobs - credentials). CR-017: keep it
  // null-able (not coerced to 0) so shortageCell's null -> '—' no-data guard
  // governs uniformly — a genuinely-missing shortage reads '—' (unknown),
  // not '0' (a definite claim).
  const lensShortage = metric.lensShortage ?? null;

  return (
    <tr
      className={`group border-b border-border-light text-sm transition-colors hover:bg-ice ${
        onSelect ? 'cursor-pointer focus:outline-none focus-within:bg-ice' : ''
      }`}
      // onSelect makes the row activate-able. The <tr> onClick is a
      // mouse-only convenience (click any cell); the ACCESSIBLE control is a
      // real <button> inside the Metro Area cell (below), which carries the
      // keyboard + AT semantics. aria-haspopup lives on that button, not the
      // <tr> (a roleless <tr> can't meaningfully carry it).
      onClick={onSelect ? () => onSelect(metro) : undefined}
    >
      <td className="px-3 py-2.5 text-left font-mono tabular-nums text-text-4">{rank}</td>
      <td className="max-w-name-col px-3 py-2.5">
        {/* WCAG 1.3.1 / 4.1.2 (Siteimprove/Alfa sia-r46): the interactive
            affordance is a real <button> INSIDE the cell — NOT role="button"
            ON the <td>, which overrides the cell's table role and leaves the
            column header unassigned to any cell. stopPropagation so clicking
            the button doesn't ALSO trigger the row's mouse onClick. */}
        {onSelect ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(metro);
            }}
            aria-haspopup="dialog"
            className="block max-w-full truncate text-left font-medium text-text-1 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
          >
            {metro.name}
          </button>
        ) : (
          <span className="block max-w-full truncate font-medium text-text-1">{metro.name}</span>
        )}
      </td>

      {lensActive ? (
        <>
          {/* 2026-07-02 (V2 feedback): data-label strings updated to
              match the full column header wording per the recurring
              column-name spec. The mobile card view surfaces
              data-label as a pseudo-element caption. */}
          <td
            data-label="Annual credentials awarded"
            className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
          >
            {formatNumber(lensCredentials)}
          </td>
          <td
            data-label="Annual job openings"
            className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
          >
            {formatNumber(lensJobs)}
          </td>
          <td data-label="Credentials-to-jobs ratio" className="px-3 py-2.5 text-left">
            <RankingsSeverityPill ratio={ratio} />
          </td>
          <td
            data-label="Annual credential shortage"
            className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
          >
            {shortageCell(metric.lensStatus ?? getStatus(ratio), lensShortage)}
          </td>
        </>
      ) : (
        <>
          <td
            data-label="Springboard occupational group"
            className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
          >
            {formatNumber(metric.springboardCount)}
          </td>
          <td
            data-label="Share of occupational groups with shortages"
            className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
          >
            {shortageSharePct}%
          </td>
          <td data-label="Credentials-to-jobs ratio" className="px-3 py-2.5 text-left">
            <RankingsSeverityPill ratio={ratio} />
          </td>
          <td
            data-label="Occupational group with largest shortage"
            className="max-w-[260px] truncate px-3 py-2.5 text-sm text-text-3"
          >
            {metric.mostUnderproducedGroup}
          </td>
        </>
      )}
    </tr>
  );
}
